import React from "react";
import { Box, Grid, Paper, Typography, useTheme, Button } from "@mui/material";
import FullLogo from "./layout/Logo";
import { useTheme as useCustomTheme } from "../contexts/ThemeContext";

const ColorPaletteDemo: React.FC = () => {
  const theme = useTheme();
  const { mode, toggleColorMode } = useCustomTheme();

  const colorSections = [
    {
      title: "Primary",
      colors: [
        { name: "Main", color: theme.palette.primary.main },
        { name: "Light", color: theme.palette.primary.light },
        { name: "Dark", color: theme.palette.primary.dark },
      ],
    },
    {
      title: "Secondary",
      colors: [
        { name: "Main", color: theme.palette.secondary.main },
        { name: "Light", color: theme.palette.secondary.light },
        { name: "Dark", color: theme.palette.secondary.dark },
      ],
    },
    {
      title: "Error",
      colors: [
        { name: "Main", color: theme.palette.error.main },
        { name: "Light", color: theme.palette.error.light },
        { name: "Dark", color: theme.palette.error.dark },
      ],
    },
    {
      title: "Success",
      colors: [
        { name: "Main", color: theme.palette.success.main },
        { name: "Light", color: theme.palette.success.light },
        { name: "Dark", color: theme.palette.success.dark },
      ],
    },
    {
      title: "Info",
      colors: [
        { name: "Main", color: theme.palette.info.main },
        { name: "Light", color: theme.palette.info.light },
        { name: "Dark", color: theme.palette.info.dark },
      ],
    },
  ];

  return (
    <Box sx={{ p: 4 }}>
      <Box
        sx={{
          mb: 4,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <FullLogo
          height={60}
          color={mode === "light" ? "#174B67" : "#FFFFFF"}
        />
        <Button variant="contained" color="secondary" onClick={toggleColorMode}>
          Toggle {mode === "light" ? "Dark" : "Light"} Mode
        </Button>
      </Box>

      <Typography variant="h4" gutterBottom>
        CanYa Color Palette - {mode.charAt(0).toUpperCase() + mode.slice(1)}{" "}
        Mode
      </Typography>

      <Grid container spacing={3}>
        {colorSections.map((section) => (
          <Grid item xs={12} sm={6} md={4} key={section.title}>
            <Paper elevation={2} sx={{ p: 2, height: "100%" }}>
              <Typography variant="h6" gutterBottom>
                {section.title}
              </Typography>
              {section.colors.map((color) => (
                <Box key={color.name} sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      height: 50,
                      width: "100%",
                      bgcolor: color.color,
                      borderRadius: 1,
                      mb: 1,
                    }}
                  />
                  <Typography variant="body2">
                    {color.name}: {color.color}
                  </Typography>
                </Box>
              ))}
            </Paper>
          </Grid>
        ))}

        <Grid item xs={12} sm={6} md={4}>
          <Paper elevation={2} sx={{ p: 2, height: "100%" }}>
            <Typography variant="h6" gutterBottom>
              Background
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Box
                sx={{
                  height: 50,
                  width: "100%",
                  bgcolor: theme.palette.background.default,
                  borderRadius: 1,
                  border: 1,
                  borderColor: "divider",
                  mb: 1,
                }}
              />
              <Typography variant="body2">
                Default: {theme.palette.background.default}
              </Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Box
                sx={{
                  height: 50,
                  width: "100%",
                  bgcolor: theme.palette.background.paper,
                  borderRadius: 1,
                  border: 1,
                  borderColor: "divider",
                  mb: 1,
                }}
              />
              <Typography variant="body2">
                Paper: {theme.palette.background.paper}
              </Typography>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Paper elevation={2} sx={{ p: 2, height: "100%" }}>
            <Typography variant="h6" gutterBottom>
              Text
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body1" color="text.primary">
                Primary Text
              </Typography>
              <Typography variant="body2">
                Color: {theme.palette.text.primary}
              </Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body1" color="text.secondary">
                Secondary Text
              </Typography>
              <Typography variant="body2">
                Color: {theme.palette.text.secondary}
              </Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body1" color="text.disabled">
                Disabled Text
              </Typography>
              <Typography variant="body2">
                Color: {theme.palette.text.disabled}
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ColorPaletteDemo;
